import type { ChangeEvent, FormEvent, ReactNode } from 'react'

interface FieldProps {
  id: string
  label: string
  type: 'email' | 'password' | 'text' | 'tel'
  value: string
  onChange: (value: string) => void
  autoComplete?: string
  required?: boolean
  minLength?: number
  placeholder?: string
  /** Helper text rendered under the label (e.g. "Optional"). */
  hint?: string
  /** Small line of feedback under the input (validation message, etc). */
  feedback?: { kind: 'ok' | 'err'; text: string } | null
}

export function Field({
  id,
  label,
  type,
  value,
  onChange,
  autoComplete,
  required = true,
  minLength,
  placeholder,
  hint,
  feedback,
}: FieldProps) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1.5">
      <span className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">
          {label}
        </span>
        {hint ? (
          <span className="text-[11px] font-normal normal-case text-[var(--color-muted)]/80">
            {hint}
          </span>
        ) : null}
      </span>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        placeholder={placeholder}
        className={[
          'w-full',
          'px-3 py-2.5 rounded-[var(--radius-DEFAULT)]',
          'bg-[var(--color-surface)]',
          'border',
          feedback?.kind === 'err'
            ? 'border-[var(--color-signal)]'
            : 'border-[var(--color-border)]',
          'text-[14px] text-[var(--color-text)]',
          'placeholder:text-[var(--color-muted)]/60',
          'focus:outline-none focus:border-[var(--color-accent)]',
          'focus:ring-2 focus:ring-[var(--color-accent)]/30',
          'transition-colors',
        ].join(' ')}
      />
      {feedback ? (
        <span
          className={[
            'text-[11px]',
            feedback.kind === 'ok'
              ? 'text-[var(--color-accent)]'
              : 'text-[var(--color-signal)]',
          ].join(' ')}
        >
          {feedback.text}
        </span>
      ) : null}
    </label>
  )
}

interface AuthFormProps {
  onSubmit: (e: FormEvent<HTMLFormElement>) => void
  children: ReactNode
  submitLabel: string
  submitting?: boolean
  error?: string | null
}

export function AuthForm({
  onSubmit,
  children,
  submitLabel,
  submitting = false,
  error = null,
}: AuthFormProps) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      {children}
      {error ? (
        <p
          role="alert"
          className="text-[13px] text-[var(--color-signal)] bg-[var(--color-signal-soft)] px-3 py-2 rounded-[var(--radius-DEFAULT)]"
        >
          {error}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={submitting}
        className={[
          'inline-flex items-center justify-center gap-2',
          'mt-2 px-4 py-2.5 rounded-[var(--radius-DEFAULT)]',
          'bg-[var(--color-accent)] text-[var(--color-surface)]',
          'text-[14px] font-medium cursor-pointer',
          'hover:bg-[var(--color-accent-hover)] transition-colors',
          'disabled:opacity-60 disabled:cursor-not-allowed',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-elev)]',
        ].join(' ')}
      >
        {submitting ? 'Working…' : submitLabel}
      </button>
    </form>
  )
}
